import {
  EVENT_SOURCE,
  HOLDOUT,
  getAssignmentBucket,
  getBucket,
  getBucketMap,
  sendExperimentTriggerThroughKafkaRestProxy,
} from "@outschool/experiments-shared";
import { usePageContext, useTrackEvent } from "@outschool/ui-analytics";
import isEqual from "lodash/isEqual";
import React from "react";

import { useServerExperimentContext } from "./ExperimentsServiceProvider";

export const CONTROL_VARIANT = "__control";

function readNonZeroNaturalNumber(
  envVar: string | undefined,
  defaultValue: number | undefined = undefined,
) {
  if (!envVar) {
    return undefined;
  }
  const n = Number.parseInt(envVar, 10);
  return Number.isFinite(n) && n > 0 ? n : defaultValue;
}

// This determines how users are bucketed and assigned to variants
// if we ever change this, we should restart any in progress experiments
const HASH_SEED = 262626;

type TriggerValue = {
  variantAssigned: string;
  // Additional properties to pass along to the trigger event for analytics tracking
  additionalProperties?: Record<any, any>;
};

type ContentfulVariantType = {
  // If name is nullish, we ignore this variant as an option
  name?: string | null;
  // If experimentVersion is nullish, we ignore this variant as an option
  experimentVersion?: number | null;
};

type ContentfulVariantTypeNonNull = {
  name: string;
  experimentVersion: number;
};

type ContentfulExperimentProps = {
  // The Contentful content entities to which we will randomly assign users
  variants: ContentfulVariantType[];
  // If true, we will add a "control" variant to the `variants` array when determining assignment
  shouldIncludeControlGroup: boolean;
  // The % of a holdout group that should not be assigned to an experiment variant
  holdoutPct?: string | number;
  // For passing to the experiment data, references the context in which the hook was called
  eventSource: EVENT_SOURCE;
};

/*
  Randomly assigns users to "variants" based on published pieces of content in Contentful.
  i.e. 
    - I have two "Home page banner" content entities defined in Contentful
    - I fetch both "Home page banner"s from Contentful's GQL and pass them to this hook as the `variants` prop
    - I receive back a `variantAssigned` (one of the "Home page banner"s) and a `trigger` function
    - I can then render the assigned "Home page banner" `variantAssigned` to the user
      - This function is deterministic based on the `loggedOutUserExperimentUid`, so a particular anon user will always see the same variant
    - I can then also call the `trigger` function at the inflection point at which we render the treatment experience
      - This will populate into our `experiment_triggers` analytics tables, and we can use our experiment dashboards to evaluate impact
*/
export default function useContentfulExperiment({
  variants,
  shouldIncludeControlGroup,
  holdoutPct,
  eventSource,
}: ContentfulExperimentProps) {
  let holdoutPctParsed: number = 0;
  if (typeof holdoutPct === "string") {
    const holdoutPctStrToNum = readNonZeroNaturalNumber(holdoutPct);
    if (holdoutPctStrToNum !== undefined) {
      holdoutPctParsed = holdoutPctStrToNum;
    }
  } else if (holdoutPct !== undefined) {
    holdoutPctParsed = holdoutPct;
  }
  if (holdoutPctParsed < 0 || holdoutPctParsed > 1) {
    holdoutPctParsed = 0;
  }

  const trackEvent = useTrackEvent();
  const { loggedInUserExperimentUid, loggedOutUserExperimentUid } =
    useServerExperimentContext();
  const experimentTriggers = React.useRef<Record<string, TriggerValue>>({});
  const page = usePageContext();

  const variantsFiltered = variants.filter(
    (b: ContentfulVariantType): b is ContentfulVariantTypeNonNull =>
      !!b.name &&
      b.experimentVersion !== null &&
      b.experimentVersion !== undefined,
  );

  const experimentName = (
    variantsFiltered.map(b => `${b.name}.v${b.experimentVersion}`) ?? []
  ).join("-");

  const variantAssigned = React.useMemo(() => {
    if (variantsFiltered.length === 0) {
      return HOLDOUT;
    }
    const bucketMap = getBucketMap({
      experimentName,
      exposurePct: 1 - holdoutPctParsed,
      variants: variantsFiltered
        .map(b => b.name)
        .concat(shouldIncludeControlGroup ? [CONTROL_VARIANT] : [])
        .sort(),
    });
    const assignmentBucket = getAssignmentBucket(
      loggedOutUserExperimentUid!,
      HASH_SEED,
    );
    return getBucket({
      assignmentBucket,
      bucketMap,
    });
  }, [
    experimentName,
    variantsFiltered,
    loggedOutUserExperimentUid,
    shouldIncludeControlGroup,
    holdoutPctParsed,
  ]);

  const shouldTrigger = React.useCallback(
    ({
      experimentName,
      variantAssigned,
      additionalProperties,
    }: {
      experimentName: string;
      variantAssigned: string;
      additionalProperties?: Record<any, any>;
    }) => {
      if (!variantAssigned || variantAssigned === HOLDOUT) {
        return false;
      }
      const triggerValue = {
        variantAssigned,
        additionalProperties,
      };
      if (isEqual(experimentTriggers.current[experimentName], triggerValue)) {
        return false;
      }
      experimentTriggers.current[experimentName] = triggerValue;
      return true;
    },
    [],
  );

  const trigger = React.useCallback(
    ({
      additionalProperties,
    }: {
      additionalProperties?: Record<string, any>;
    } = {}) => {
      if (shouldTrigger({ experimentName, variantAssigned })) {
        sendExperimentTriggerThroughKafkaRestProxy(
          {
            experiment_name: experimentName,
            variant_assigned: variantAssigned,
            logged_out_user_experiment_uid: loggedOutUserExperimentUid,
            logged_in_user_experiment_uid: loggedInUserExperimentUid,
            page_category: page.pageCategory,
            page_name: page.pageName,
            user_id: loggedInUserExperimentUid,
            additional_properties: JSON.stringify(additionalProperties),
          },
          eventSource,
        ).then(() => {
          trackEvent("Experiment Triggered", {
            experiment_name: experimentName,
            experiment_variant: variantAssigned,
          });
        });
      }
    },
    [
      experimentName,
      variantAssigned,
      loggedInUserExperimentUid,
      loggedOutUserExperimentUid,
      page.pageName,
      page.pageCategory,
      eventSource,
      shouldTrigger,
      trackEvent,
    ],
  );

  return {
    variantAssigned,
    trigger,
    isInTreatment:
      variantAssigned !== CONTROL_VARIANT && variantAssigned !== HOLDOUT,
  };
}
